import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from '@remix-run/react';
import { useAtom } from 'jotai';
import { useStatsNumbers } from '@modules/root';
import { generateURL } from '@modules-rocco/url/api/generate-url';
import { SEARCH_CATEGORIES } from '@modules-rocco/search/config/search-service.config';
import { useSearchInput } from '@rocco/ui/search/hooks/use-search-input';
import { SearchBar } from '@rocco/ui/search/components/SearchBar';
import { useSearchQuery } from '@ui-rocco/hooks/use-search-query';
import { useRecentlySearched } from '@modules-rocco/search/hook/use-recently-searched';
import type {
    SearchSuggestion,
    SearchSuggestionGroup,
} from '@rocco/ui/search/view-models/search-suggestion';
import { SearchSuggestionList } from '@rocco/ui/search/components/SearchSuggestionList';
import { isCategorySuggestion } from '@rocco/ui/search/utils/type-guards';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
} from '@archipro/rocco/components/sheet';
import { uiStateAtomSearchModalOpened } from '../states/ui-state-search-modal-opened';
import { ReactComponent as CloseIcon } from '@rocco/icons/svg/close.svg';
import {
    formatNumberWithCommas,
    roundToNearest,
} from '@rocco/utils/format-number';
import { MobileSearchTrending } from '../components/MobileSearchTrending';
import { useSearchTracking } from '~/ui-rocco/hooks/use-search-tracking';
import { useTrendingSearchResults } from '../hooks/use-trending-search-results';

type MobileSearchModalView = 'recentlySearched' | 'suggestions';

function getSearchPath(pathname: string): string {
    const pathToCategoryMap: Record<string, string> = {
        '/projects': '/search/category_projects',
        '/professionals': '/search/category_professionals',
        '/products': '/search/category_products',
        '/articles': '/search/category_articles',
    };

    return pathToCategoryMap[pathname] || '/search';
}

export function MobileSearchModal() {
    const [view, setView] = useState<MobileSearchModalView>('recentlySearched');
    const [isModalOpen, setIsModalOpen] = useAtom(uiStateAtomSearchModalOpened);
    const location = useLocation();
    const { addRecentSearch } = useRecentlySearched();
    const [, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { statsNumbers } = useStatsNumbers();
    // Preload trending search results.
    useTrendingSearchResults();

    // web-platform specific search functions
    const query = useSearchQuery('');
    const { clearSuggestions } = query;

    const finalPath = getSearchPath(location.pathname);

    // using rocco search input
    const searchInputResult = useSearchInput({
        query,
        doSearch: (finalQuery, searchIndex) => {
            const category = SEARCH_CATEGORIES.find(
                (c) => c.index === searchIndex
            )?.category;

            if (finalQuery) {
                setSearchParams({ search: finalQuery });
                addRecentSearch(finalQuery);
                const currentCategory = location.pathname.startsWith(
                    '/projects'
                )
                    ? 'projects'
                    : location.pathname.startsWith('/professionals')
                      ? 'professionals'
                      : location.pathname.startsWith('/products')
                        ? 'products'
                        : location.pathname.startsWith('/articles')
                          ? 'articles'
                          : '';
                const targetPath = generateURL(finalPath, {
                    search: finalQuery,
                    ...(category
                        ? { category }
                        : currentCategory
                          ? { category: currentCategory }
                          : {}),
                });
                navigate(targetPath);
            } else {
                setSearchParams({});
                navigate(finalPath);
            }
        },
        isSearchPage: location.pathname.startsWith('/search'),
        clearSuggestionsOnSearch: false,
    });

    const {
        searchQuery: inputSearchQuery,
        handleInputChange,
        handleKeyPress,
        handleSearch,
        suggestionGroups,
        clearSearchQuery,
    } = searchInputResult;

    // Store clearSearchQuery in a ref to avoid recreating the useEffect
    const clearSearchQueryRef = useRef(clearSearchQuery);
    clearSearchQueryRef.current = clearSearchQuery;

    useEffect(() => {
        const hasSuggestions = suggestionGroups.some(
            (group) => group.options.length > 0
        );

        if (inputSearchQuery && hasSuggestions) {
            setView('suggestions');
        } else {
            setView('recentlySearched');
        }
    }, [inputSearchQuery, suggestionGroups]);

    useEffect(() => {
        // close the modal on url change
        setIsModalOpen(false);
        clearSearchQueryRef.current();
    }, [location, setIsModalOpen]);

    // Clear suggestions when modal is closed
    useEffect(() => {
        if (!isModalOpen) {
            clearSuggestions();
        }
    }, [isModalOpen, clearSuggestions]);

    const placeholderTexts = useMemo(() => {
        if (!statsNumbers) {
            return ['Search'];
        }
        return [
            `Search ${formatNumberWithCommas(roundToNearest(statsNumbers.ProductCount))}+ products`,
            `Search ${formatNumberWithCommas(roundToNearest(statsNumbers.ProfessionalCount))}+ professionals`,
            `Search ${formatNumberWithCommas(roundToNearest(statsNumbers.ProjectCount))}+ projects`,
        ];
    }, [statsNumbers]);

    const searchSectionInputRef = useRef<HTMLInputElement>(null);

    return (
        <Sheet open={isModalOpen} onOpenChange={setIsModalOpen}>
            <SheetContent
                side="left"
                radius="none"
                hideDefaultCloseButton
                noOverlay
                zLevel="top"
            >
                {/* Hidden title and description to suppress console warning. */}
                <SheetTitle className="hidden" />
                <SheetDescription className="hidden" />
                <div className="h-dvh w-dvw overflow-y-auto bg-white px-6 py-6">
                    <div className="flex flex-col gap-y-9.5">
                        <CloseIcon
                            role="button"
                            onClick={() => setIsModalOpen(false)}
                            aria-label="Close Search Modal"
                        />

                        <div className="flex flex-col gap-y-8">
                            <SearchBar
                                key="searchpage-searchBar"
                                ref={searchSectionInputRef}
                                placeholderTexts={placeholderTexts}
                                value={inputSearchQuery}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyPress}
                                onSearchSubmit={(value) =>
                                    value &&
                                    typeof value === 'string' &&
                                    handleSearch(value)
                                }
                                suggestionGroups={suggestionGroups}
                                onSearchSuggestionClick={(suggestion) => {
                                    if (isCategorySuggestion(suggestion)) {
                                        navigate(suggestion.link);
                                    } else {
                                        handleSearch(suggestion);
                                    }
                                }}
                                clearSearchQuery={() => {
                                    searchSectionInputRef.current?.focus();
                                    clearSearchQuery();
                                }}
                                displayMode={'mobile'}
                                variant={'searchPageMobile'}
                                hideSubmitButton={true}
                                disableDropdown={true}
                            />

                            {view === 'recentlySearched' && (
                                <>
                                    <MobileSearchTrending context="modal" />
                                    <MobileSearchPageRecentlySearched />
                                </>
                            )}
                            {view === 'suggestions' &&
                                suggestionGroups[0] &&
                                suggestionGroups
                                    .filter((group) => group.options.length > 0)
                                    .map((group) => (
                                        <MobileSearchPageSuggestedGroups
                                            key={group.type}
                                            query={inputSearchQuery}
                                            group={group}
                                        />
                                    ))}
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

// ============================================================================
// Local Page Components (Sections)
// ============================================================================

const MobileSearchPageRecentlySearched = () => {
    const { recentlySearched, clearHistory } = useRecentlySearched();

    const [, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const finalPath = getSearchPath(location.pathname);
    const trackingConfig = useSearchTracking({ isSearchPage: true });
    const handleSearch = (search: string) => {
        setSearchParams({ search });
        navigate(
            generateURL(finalPath, {
                search,
            })
        );
    };

    if (recentlySearched.length === 0) return null;

    return (
        <MobileSearchList
            onItemClick={(value) => {
                handleSearch(value);
                trackingConfig.onRecentSearchClick(value);
            }}
            title="Recently Searched"
            list={recentlySearched}
            extraItem={
                <button className="font-medium" onClick={clearHistory}>
                    Clear History
                </button>
            }
        />
    );
};

const MobileSearchPageSuggestedGroups = ({
    query,
    group,
}: {
    query: string;
    group: SearchSuggestionGroup;
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const finalPath = getSearchPath(location.pathname);

    const onSearchSuggestionClick = (suggestion: SearchSuggestion | string) => {
        if (isCategorySuggestion(suggestion)) {
            navigate(suggestion.link);
        } else {
            navigate(
                generateURL(finalPath, {
                    search: suggestion,
                })
            );
        }
    };

    return (
        <SearchSuggestionList
            group={group}
            query={query}
            onOptionClick={(suggestion) => onSearchSuggestionClick(suggestion)}
        />
    );
};

const MobileSearchList = ({
    onItemClick,
    title,
    list,
    extraItem,
}: {
    onItemClick: (value: string) => void;
    title: string;
    list: string[];
    extraItem?: React.ReactNode;
}) => {
    return (
        <div className="">
            <h3 className="text-base font-medium">{title}</h3>
            <ul className="my-4.5 space-y-2.5">
                {list.map((item, idx) => (
                    <li key={idx} className="w-auto text-left">
                        <button
                            className="line-clamp-1 block min-h-4.5 max-w-full overflow-hidden truncate whitespace-nowrap text-left leading-[1.1] text-[#9b9b9b]"
                            onClick={() => onItemClick(item)}
                        >
                            {item}
                        </button>
                    </li>
                ))}
            </ul>
            {extraItem}
        </div>
    );
};
